import type { ComponentProps } from "react";
import { createContext, useContext, useState } from "react";
import rehypeTruncate from "rehype-truncate";
import { tv } from "tailwind-variants";

import { analytics } from "@/config/analytics";
import { useCareplanContext } from "@/domains/careplan/contexts/use-careplan-context";
import { isCurrentProduct, isPreviousProduct } from "@/domains/careplan/factories";
import { useCareplanOrderLine } from "@/domains/careplan/hooks/use-careplan-order";
import { useRestoreCareplanCartLine } from "@/domains/careplan/hooks/use-restore-careplan-cart-line";
import {
  CAREPLAN_CHANGE_ATTRIBUTES,
  UPDATED_INSTRUCTIONS_MARKDOWN_STYLES,
} from "@/domains/careplan/models/constants";
import type {
  CareplanProductDataModel,
  PreviousCareplanProductDataModel,
} from "@/domains/careplan/models/types";
import { getCartLinkState } from "@/domains/careplan/utils/cart-link-state";
import { formatRating, formatReviewCount } from "@/domains/careplan/utils/rating";
import { isCartProcessing, useCartStore } from "@/stores";
import {
  Accordion,
  Card,
  Carousel as UICarousel,
  focusRing,
  Heading,
  type HeadingProps,
  icons,
  MarkdownRenderer,
  Modal,
  Reveal,
  Stars,
} from "@/ui";

const productCardVariants = tv({
  slots: {
    name: undefined,
    image:
      "h-48 w-full rounded-2xl bg-product-media object-contain sm:aspect-square sm:h-auto sm:w-60 sm:object-cover",
    carousel: "overflow-hidden rounded-2xl",
    description: "text-base text-content-secondary sm:text-xl",
    price: "text-xl font-semibold text-content-secondary sm:text-2xl",
    /* Tight line boxes so the gaps around the name measure exactly what the card sets. */
    /* Same color as the card's body copy, 18px on desktop. */
    format: "text-base leading-none font-semibold text-content-secondary md:text-lg",
    updatedAt: "p-1 text-base sm:text-xl",
    whyRecommended: "text-lg font-semibold text-content-secondary sm:text-xl",
    rating: "flex items-center gap-1.5 text-base leading-none text-content md:text-lg",
    carePlanPrice: "flex flex-col items-end md:flex-row md:items-baseline md:gap-2",
    carePlanPriceAmount: "text-xl font-semibold text-content-secondary sm:text-2xl",
    carePlanPriceSuffix: "text-base",
    carePlanListPrice: "mt-px text-base text-content-subtle line-through md:mt-0 md:text-lg",
    skeleton: "rounded bg-surface-muted",
    cartLinkRoot: "flex shrink-0",
    /*
      44px target with the visual kept tight: the vertical padding makes a one-line link 44px tall
      and the negative margins give that height back, so a link that wraps to two lines on a
      narrow phone grows instead of overlapping its neighbors.
    */
    cartLink: [
      "-my-2.5 inline-flex cursor-pointer flex-wrap items-center gap-x-1.5 rounded-sm py-2.5 text-left text-base underline-offset-4 transition-colors",
      "hover:underline disabled:cursor-not-allowed disabled:text-content-subtle",
      focusRing,
    ],
    cartLinkPrefix: "font-normal text-content-secondary",
    /* The action and its arrow stay together when the link wraps. */
    cartLinkAction: "inline-flex items-center gap-1 font-semibold whitespace-nowrap text-content",
    /* A state with nothing to press, such as an order still processing. */
    cartLinkText: "text-base whitespace-nowrap text-content-secondary",
  },
  variants: {
    recommended: {
      true: {
        updatedAt: "bg-accent-surface text-content-secondary",
      },
      false: {
        updatedAt: "bg-danger-surface text-content",
      },
    },
  },
});

const {
  carePlanListPrice,
  carePlanPrice,
  carePlanPriceAmount,
  carePlanPriceSuffix,
  carousel,
  cartLink,
  cartLinkAction,
  cartLinkPrefix,
  cartLinkRoot,
  cartLinkText,
  description,
  format,
  image,
  name,
  price,
  rating,
  skeleton,
  updatedAt,
  whyRecommended,
} = productCardVariants();

export type ProductCardProps = {
  product: CareplanProductDataModel | PreviousCareplanProductDataModel;
} & ComponentProps<"div">;

type ProductCardContextType = (CareplanProductDataModel | PreviousCareplanProductDataModel) & {
  selectedDetailSlug: string | null;
  setSelectedDetailSlug: (slug: string | null) => void;
};

const ProductCardContext = createContext<ProductCardContextType | null>(null);

export const useProductCardContext = () => {
  const product = useContext(ProductCardContext);
  if (!product) {
    throw Error("useProductCardContext can only be used inside a ProductCardContext.Provider");
  }

  return product;
};

const UpdatedAt = ({ className }: ComponentProps<"p">) => {
  const careplan = useCareplanContext();
  const product = useProductCardContext();
  const previousProduct = isPreviousProduct(product);
  const currentProduct = isCurrentProduct(product);

  if (previousProduct) {
    return <p className={updatedAt({ className, recommended: false })}>No Longer Recommended</p>;
  }

  if (currentProduct) {
    const hasChanges = !!currentProduct.changes?.length;

    const isNewRecommendation = !!currentProduct.changes?.some((change) => {
      return change.attribute === CAREPLAN_CHANGE_ATTRIBUTES.PRODUCT;
    });

    if (!hasChanges) {
      return null;
    }

    return (
      <p className={updatedAt({ className, recommended: true })}>
        {isNewRecommendation ? "New Recommendation" : `Updated ${careplan.updatedAt}`}
      </p>
    );
  }
};

const WhyRecommended = ({ className }: ComponentProps<"p">) => {
  const product = useProductCardContext();

  if (!product.whyRecommended) {
    return null;
  }

  return <p className={whyRecommended({ className })}>{product.whyRecommended.title}</p>;
};

const Name = ({ className, ...props }: HeadingProps) => {
  const product = useProductCardContext();

  return (
    <Heading className={name({ className })} {...props}>
      {product.name}
    </Heading>
  );
};

const Price = ({ className, ...props }: ComponentProps<"p">) => {
  const product = useProductCardContext();

  return (
    <p className={price({ className })} {...props}>
      ${product.priceAsString}
    </p>
  );
};

const Rating = ({ className, ...props }: ComponentProps<"p">) => {
  const product = useProductCardContext();
  const currentProduct = isCurrentProduct(product);

  if (!currentProduct || currentProduct.rating === null || currentProduct.reviewCount === null) {
    return null;
  }

  return (
    <p className={rating({ className })} {...props}>
      <Stars className="md:h-[18px] md:w-auto" rating={currentProduct.rating} size={16} />
      <span className="font-bold">{formatRating(currentProduct.rating)}</span>
      <span className="text-content-secondary">
        {`(${formatReviewCount(currentProduct.reviewCount)})`}
      </span>
    </p>
  );
};

/*
  The care plan price: list minus the $5 care plan code and the 10% subscription discount, read
  from the Shopify cart so the card, the cart and checkout agree. Skeletons while the cart loads;
  the plan's own list price, with no strike-through, if the cart fails.
*/
const CarePlanPrice = ({ className, ...props }: ComponentProps<"div">) => {
  const product = useProductCardContext();
  const currentProduct = isCurrentProduct(product);
  const { isLoading, line } = useCareplanOrderLine(
    currentProduct ? currentProduct.variantId : null,
  );

  if (!currentProduct) {
    return null;
  }

  if (isLoading) {
    return (
      <div className={carePlanPrice({ className })} {...props}>
        <span aria-hidden="true" className={skeleton({ className: "h-6 w-14 sm:h-7 sm:w-16" })} />
        <span aria-hidden="true" className={skeleton({ className: "mt-1 h-4 w-9 md:mt-0" })} />
        <span className="sr-only">Loading price</span>
      </div>
    );
  }

  if (!line) {
    return (
      <div className={carePlanPrice({ className })} {...props}>
        <p className={carePlanPriceAmount()}>${currentProduct.priceAsString}</p>
      </div>
    );
  }

  return (
    <div className={carePlanPrice({ className })} {...props}>
      <p className={carePlanPriceAmount()}>
        ${line.unitPriceAsString}
        <span className={carePlanPriceSuffix()}>/mo</span>
      </p>
      <p className={carePlanListPrice()}>
        <span className="sr-only">Retail price </span>${line.unitListPriceAsString}
      </p>
    </div>
  );
};

type CartLinkProps = {
  className?: string;
  /** One-line form for the desktop price row: subscription states show the action alone. */
  compact?: boolean;
};

/*
  Where this product stands with the patient's order, in the cart's own words: "Remove or Edit
  Quantity" before purchase, "Not in your order. Add to Cart" once removed, and for a
  subscriber "Subscribed. Manage Subscription", "Subscription paused. Resume", "Subscription
  ended. Reactivate" or "New to your plan. Update Subscription". See `getCartLinkState`.
*/
const CartLink = ({ className, compact = false }: CartLinkProps) => {
  const product = useProductCardContext();
  const currentProduct = isCurrentProduct(product);
  const { isLoading, line, order } = useCareplanOrderLine(
    currentProduct ? currentProduct.variantId : null,
  );
  const openCart = useCartStore((state) => {
    return state.openCart;
  });
  const isProcessing = useCartStore(isCartProcessing);
  const { isPending, restore } = useRestoreCareplanCartLine();

  if (!currentProduct) {
    return null;
  }

  if (isLoading) {
    return (
      <div className={cartLinkRoot({ className })}>
        <span aria-hidden="true" className={skeleton({ className: "h-5 w-36" })} />
      </div>
    );
  }

  if (!line || !order) {
    return null;
  }

  const state = getCartLinkState({
    line,
    subscriptionStatus: order.subscriptionStatus,
    isProcessing,
  });

  const copy = compact ? state.compact : state;

  if (state.intent === "none") {
    return (
      <div className={cartLinkRoot({ className })}>
        <p className={cartLinkText()}>{copy.prefix}</p>
      </div>
    );
  }

  const isRestore = state.intent === "restore";
  const isRestoreDisabled = isRestore && isPending;

  const handleClick = () => {
    if (isRestore) {
      analytics.track("careplan_product_add_to_cart_clicked", { sku: currentProduct.sku });
      restore(order.cartId, line);

      return;
    }

    analytics.track("careplan_product_view_cart_clicked", { sku: currentProduct.sku });
    openCart();
  };

  return (
    <div className={cartLinkRoot({ className })}>
      <button
        className={cartLink()}
        disabled={isRestoreDisabled}
        onClick={handleClick}
        type="button"
      >
        {copy.prefix ? <span className={cartLinkPrefix()}>{copy.prefix}</span> : null}
        <span className={cartLinkAction()}>
          {copy.action}
          {isRestore ? (
            <icons.PlusIcon aria-hidden="true" className="size-4" />
          ) : (
            <icons.ArrowRight aria-hidden="true" className="size-4" />
          )}
        </span>
      </button>
    </div>
  );
};

const Format = ({ className, ...props }: ComponentProps<"p">) => {
  const product = useProductCardContext();
  const currentProduct = isCurrentProduct(product);

  if (!currentProduct) {
    return null;
  }

  return (
    <p className={format({ className })} {...props}>
      {currentProduct.format}
    </p>
  );
};

const Carousel = ({ className }: ComponentProps<"div">) => {
  const product = useProductCardContext();

  return (
    <UICarousel.Root
      className={carousel({ className })}
      renderSlide={({ id, url }) => {
        return (
          <div
            className="flex w-full flex-none items-center justify-center rounded-2xl select-none last:pr-0"
            key={id}
          >
            <img
              alt={product.name}
              className="h-80 w-full rounded-xl bg-product-media object-contain"
              src={url}
            />
          </div>
        );
      }}
      slides={product.images.map((url, index) => {
        return {
          url,
          id: index,
        };
      })}
    >
      <UICarousel.Slider className="overflow-visible">
        <UICarousel.Slides />
      </UICarousel.Slider>
      <UICarousel.ArrowNavigation>
        <UICarousel.PrevButton />
        <UICarousel.NextButton />
      </UICarousel.ArrowNavigation>
    </UICarousel.Root>
  );
};

const Description = ({ className, ...props }: ComponentProps<"p">) => {
  const product = useProductCardContext();

  return (
    <p className={description({ className })} {...props}>
      {product.description}
    </p>
  );
};

const Instructions = () => {
  const WHY_THIS_ACCORDION_KEY = "why-this";
  const HOW_IT_WORKS_MODAL_SLUG = "how-it-works";

  const { setSelectedDetailSlug, ...product } = useProductCardContext();
  const [isExpanded, setIsExpanded] = useState(false);

  const currentProduct = isCurrentProduct(product);

  if (!currentProduct) {
    return null;
  }

  const instructionsChanged = !!currentProduct.changes?.some((change) => {
    return (
      change.attribute === CAREPLAN_CHANGE_ATTRIBUTES.DOSAGE ||
      change.attribute === CAREPLAN_CHANGE_ATTRIBUTES.FREQUENCY
    );
  });

  const howItWorksDetail = currentProduct.details.find((detail) => {
    return detail.slug === HOW_IT_WORKS_MODAL_SLUG;
  });

  return (
    <div className="rounded-xl border border-line">
      {currentProduct.whyRecommended?.content ? (
        <Accordion.Root
          onValueChange={(value) => {
            if (value === WHY_THIS_ACCORDION_KEY) {
              analytics.track("product_why_recommended_viewed", {
                sku: currentProduct.sku,
              });
            }

            return setIsExpanded(value === WHY_THIS_ACCORDION_KEY);
          }}
          type="single"
          collapsible
        >
          <Accordion.Item className="rounded-t-xl bg-surface-sunken" value={WHY_THIS_ACCORDION_KEY}>
            <Accordion.Header className="flex flex-col px-4">
              <Accordion.Trigger className="text-base font-bold text-content-secondary sm:text-xl">
                Why this?
                <Accordion.Icon />
              </Accordion.Trigger>
              <Reveal>
                <div className="pb-4 text-base text-content-secondary sm:text-xl">
                  <MarkdownRenderer
                    rehypePlugins={[[rehypeTruncate, { maxChars: 200, disable: isExpanded }]]}
                  >
                    {currentProduct.whyRecommended?.content}
                  </MarkdownRenderer>
                </div>
              </Reveal>
            </Accordion.Header>

            <Accordion.Content className="flex flex-col gap-4 px-4">
              {howItWorksDetail?.body ? (
                <button
                  className={`w-fit cursor-pointer rounded-md py-1 font-semibold underline underline-offset-4 transition-colors hover:text-accent ${focusRing}`}
                  onClick={() => {
                    return setSelectedDetailSlug(HOW_IT_WORKS_MODAL_SLUG);
                  }}
                  type="button"
                >
                  See How It Works For Your Symptoms
                </button>
              ) : null}
            </Accordion.Content>
          </Accordion.Item>
        </Accordion.Root>
      ) : null}
      <div className="p-4 text-base text-content-secondary sm:text-xl">
        <MarkdownRenderer
          components={instructionsChanged ? UPDATED_INSTRUCTIONS_MARKDOWN_STYLES : undefined}
        >
          {currentProduct.usageInstructions}
        </MarkdownRenderer>
      </div>
    </div>
  );
};

const DosingAndEffects = () => {
  const product = useProductCardContext();
  const currentProduct = isCurrentProduct(product);

  if (!currentProduct) {
    return null;
  }

  return (
    <div className="overflow-hidden rounded-xl border border-line text-center text-base text-content-secondary sm:text-lg">
      <table className="w-full border-collapse">
        <tbody>
          <tr className="divide-x divide-line border-line">
            <td className="p-4">
              <div className="flex items-center justify-center gap-4">
                <icons.Onset className="hidden size-11 sm:block" />

                <div className="flex flex-col items-start">
                  <div>Effect Onset:</div>

                  <div className="font-bold">{currentProduct.effectOnset}</div>
                </div>
              </div>
            </td>

            <td className="p-4">
              <div className="flex items-center justify-center gap-4">
                <icons.Duration className="hidden size-11 sm:block" />
                <div className="flex flex-col items-start">
                  <div>Effect Duration:</div>

                  <div className="font-bold">{currentProduct.effectDuration}</div>
                </div>
              </div>
            </td>
          </tr>

          <tr className="border-t border-line">
            <td className="p-4" colSpan={2}>
              <p className="leading-6 text-content-secondary sm:text-xl">
                {currentProduct.productSupply}
              </p>
            </td>
          </tr>

          <tr className="border-t border-line">
            <td className="p-4" colSpan={2}>
              <p className="leading-6 text-content-secondary sm:text-xl">
                Contains: {currentProduct.allIngredients}
              </p>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const Image = ({ className }: ComponentProps<"img">) => {
  const product = useProductCardContext();

  return <img alt={product.name} className={image({ className })} src={product.images[0]} />;
};

const Details = () => {
  const { selectedDetailSlug, setSelectedDetailSlug, ...product } = useProductCardContext();
  const currentProduct = isCurrentProduct(product);

  if (!currentProduct) {
    return null;
  }

  const selectedDetail = currentProduct.details.find((detail) => {
    return detail.slug === selectedDetailSlug;
  });

  return (
    <>
      {!!selectedDetail && (
        <Modal
          body={<MarkdownRenderer resetConfig>{selectedDetail.body}</MarkdownRenderer>}
          className="max-h-[80vh] overflow-scroll"
          closeModal={() => {
            setSelectedDetailSlug(null);
          }}
          isOpen={!!selectedDetail}
          title={selectedDetail.title}
        />
      )}
      <div className="grid grid-cols-2 gap-3.5 lg:grid-cols-4">
        {currentProduct.details.map((detail) => {
          if (!detail.body) {
            return null;
          }

          return (
            <button
              className={`flex min-h-11 w-full cursor-pointer items-center gap-2 rounded-md bg-surface-sunken px-3 py-2 text-left font-semibold text-content-secondary transition-colors hover:bg-surface-hover hover:text-accent ${focusRing}`}
              key={detail.id}
              onClick={() => {
                setSelectedDetailSlug(detail.slug);
              }}
              type="button"
            >
              <span>{detail.shortTitle ?? detail.title}</span>
              <icons.ArrowRightIcon className="size-5 stroke-2" />
            </button>
          );
        })}
      </div>
    </>
  );
};

const Root = ({
  "aria-label": ariaLabel,
  children,
  className,
  product,
  ref,
  tabIndex,
}: ProductCardProps) => {
  const [selectedDetailSlug, setSelectedDetailSlug] = useState<string | null>(null);

  return (
    <ProductCardContext.Provider
      value={{
        ...product,
        selectedDetailSlug,
        setSelectedDetailSlug,
      }}
    >
      <Card aria-label={ariaLabel} className={className} ref={ref} tabIndex={tabIndex}>
        {children}
      </Card>
    </ProductCardContext.Provider>
  );
};

export const ProductCard = {
  Root,
  Name,
  Price,
  CarePlanPrice,
  Rating,
  CartLink,
  Format,
  UpdatedAt,
  WhyRecommended,
  Carousel,
  Description,
  DosingAndEffects,
  Image,
  Instructions,
  Details,
};
